import { useState, useEffect, useCallback, useRef } from 'react';
import { isWeChat, isIOS } from '../../utils/wechatDetector';

/**
 * Detect install platform context.
 * @returns {'wechat' | 'ios-safari' | 'android-chrome' | 'desktop'}
 */
function detectPlatform() {
  if (isWeChat()) return 'wechat';
  const ua = navigator.userAgent;
  if (isIOS()) return 'ios-safari';
  if (/android/i.test(ua)) return 'android-chrome';
  return 'desktop';
}

/**
 * Hook for "Add to Home Screen" install prompt logic.
 *
 * - Captures `beforeinstallprompt` on Android Chrome
 * - Detects platform (WeChat / iOS / Android / desktop)
 * - Detects if already running in standalone mode
 *
 * @returns {{ canInstall: boolean, isInstalled: boolean, platform: string, triggerInstall: () => Promise<void> }}
 */
export function useInstallPrompt() {
  const deferredPrompt = useRef(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already in standalone mode (installed PWA)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) {
      setIsInstalled(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setCanInstall(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Listen for successful install
    const onInstalled = () => setIsInstalled(true);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const triggerInstall = useCallback(async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    deferredPrompt.current = null;
    setCanInstall(false);
  }, []);

  const platform = detectPlatform();

  return { canInstall, isInstalled, platform, triggerInstall };
}
